import { CheckCircleIcon, FileIcon, LoaderCircle, LockIcon, XIcon } from 'lucide-react'
import { useRef, useState } from 'react'

import Card from 'apps/employer_renewals/components/card/Card'
import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { Banner, Button, FileUpload, TextLink } from 'slab'
import { type QuotingEmployeeCensus } from 'utils/web_api.schemas'

const CompletedCensusFileView = (
  { census, onExitFileClick }:
  { census: QuotingEmployeeCensus, onExitFileClick: () => void }
) => (
  <>
    <div className='body'>
      Your employee census has been successfully uploaded and processed.
    </div>
    <div className='flex items-center gap-2 border border-gray-400 border-dashed px-2 py-4 rounded-md flex justify-between'>
      <div className='flex items-center gap-2'>
        <FileIcon size={16} className='text-gray-500' />
        <span className='body'>{census.file_name}</span>
      </div>
      <Button variant='tertiary' onClick={onExitFileClick}>
        <XIcon size={16} className='text-gray-500' />
      </Button>
    </div>
  </>
)

const CensusUploadView = (
  { census, isUploading, showSuccessIcon, handleFileChange, fileInputRef, fileTypeError }:
  {
    census: QuotingEmployeeCensus | undefined,
    isUploading: boolean,
    showSuccessIcon: boolean,
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    fileInputRef: React.RefObject<HTMLInputElement>,
    fileTypeError: string | undefined
  }
) => (
  <>
    <div className='body'>
      Upload your file in .CSV format below.
    </div>
    {((census?.parsing_errors?.length ?? 0) > 0 || fileTypeError) && (
      <Banner title='Error uploading employee census' type='critical'>
        {fileTypeError}
        {!fileTypeError && census?.parsing_errors?.map(e => <li key={e}>{e}</li>)}
      </Banner>
    )}
    <FileUpload
      accept='text/csv'
      disabled={isUploading || census?.status === 'processing'}
      onChange={handleFileChange}
      ref={fileInputRef}
    >
      <FileUpload.Content
        instructions={isUploading || census?.status === 'processing' ? 'Processing...' : undefined}
        icon={
          showSuccessIcon
            ? () => <CheckCircleIcon className='text-green-500' />
            : (isUploading || census?.status === 'processing')
                ? () => <LoaderCircle className='animate-spin' />
                : undefined
        }
        details={
          <ul className='caption text-center'>
            <li>2 MB max size</li>
            <li>CSV format required</li>
          </ul>
      }
      />
    </FileUpload>
  </>
)

export default function CensusUpload ({
  census,
  censusIsLoading,
  isUploading,
  onFileUpload,
  showSuccessIconWhenCensusIsProcessed,
}: {
  census: QuotingEmployeeCensus | undefined
  censusIsLoading: boolean
  isUploading: boolean
  onFileUpload: (file: File) => Promise<void>
  showSuccessIconWhenCensusIsProcessed: boolean
}) {
  const { navigateTo } = useAppNavigation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [hasExitedSuccessState, setHasExitedSuccessState] = useState(false)
  const [fileTypeError, setFileTypeError] = useState<string | undefined>()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type !== 'text/csv') {
        setFileTypeError(`Please upload your census as a CSV file, provided file was "${file.type}"`)
        return
      } else {
        setFileTypeError(undefined)
        onFileUpload(file)
      }
      // Clear the input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  if (censusIsLoading) {
    return <div className='bg-gray-200 rounded-md animate-pulse h-44' />
  }

  const showInlineCensusFile = census?.status === 'processed' && !hasExitedSuccessState && !showSuccessIconWhenCensusIsProcessed

  return (
    <div className='flex flex-col gap-2'>
      <Card raw>
        <div className='flex flex-col gap-3 p-4'>
          {showInlineCensusFile
            ? (
              <CompletedCensusFileView
                census={census}
                onExitFileClick={() => {
                  setHasExitedSuccessState(true)
                  if (fileInputRef.current) {
                    fileInputRef.current.value = ''
                  }
                }}
              />
              )
            : (
              <CensusUploadView
                census={census}
                isUploading={isUploading}
                showSuccessIcon={census?.status === 'processed' && !hasExitedSuccessState}
                handleFileChange={handleFileChange}
                fileInputRef={fileInputRef}
                fileTypeError={fileTypeError}
              />
              )}
        </div>
      </Card>
      <Card raw>
        <div className='flex flex-row gap-2 p-4 text-gray-500 bg-gray-50'>
          <LockIcon size={14} className='shrink-0 mt-0.5' />
          <p className='body'>
            This information is only used for compliance purposes and will never be shared or sold. Our SOC 2 Type II certification means the data you share with Thatch is protected and secure. Learn more at our <TextLink href='https://trust.thatch.com/' color='purple' target='_blank' rel='noopener noreferrer'>Trust Center</TextLink>.
          </p>
        </div>
      </Card>
      <div className='mt-8'>
        <NavigationButtons
          onNext={showInlineCensusFile ? () => navigateTo(EmployerRenewalsRoute.EMPLOYEES_ELIGIBILITY) : undefined}
        />
      </div>
    </div>
  )
}
